import { QueryErrorBoundary } from '@/catalog-apps/components/QueryErrorBoundary'
import { getDatabricksErrorMessage } from '@/lib/databricksErrorMessage'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { SectionContainer } from '@/pages/shared-components/SectionContainer'
import { LoadingComponent } from '@/components/ui/LoadingComponent'
import { Suspense, useState } from 'react'
import { useCurrentWorkspace } from '../useCurrentWorkspace'
import { Button, buttonVariants } from '@/components/shadcn/button'
import { IconBg } from '@/components/ui/IconBg'
import { NoSearchResultsDisplay } from '@/components/ui/NoSeachResultsDisplay'
import { cn } from '@/lib/utils'
import { IconDatabricksOutline } from '@icons/IconDatabricksOutline'
import { IconEditOutline } from '@icons/IconEditOutline'
import { IconPlayOutline } from '@icons/IconPlayOutline'
import { toast } from 'sonner'
import { IconTransformOutline } from '@icons/IconTransformOutline'
import { TooltipButton } from '@/components/ui/TooltipButton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/shadcn/dialog'
import { Textarea } from '@/components/shadcn/textarea'
import { Label } from '@/components/shadcn/label'

type DatabricksMessagesDatabricksWorkspaceJob = RestSchemas['DatabricksMessages.DatabricksWorkspaceJob']

export function DatabricksJobsSection() {
  const currentWorkspace = useCurrentWorkspace()
  const { workspaceId } = currentWorkspace
  const { putDatabricksApiSyncDatabricksWorkspaceJobs, getDatabricksApiGetDatabricksWorkspaceStatus } = useRestClient()
  const { mutate: syncWorkspaceJobs, isPending: syncingWorkspaceJobs } =
    putDatabricksApiSyncDatabricksWorkspaceJobs.useMutation()

  const onSync = () => {
    syncWorkspaceJobs(
      { query: { workspaceId: workspaceId } },
      {
        onError: (error) => {
          toast.error('Error syncing workspace: ' + getDatabricksErrorMessage(error))
        },
        onSuccess: () => {
          toast.success('Sync done')
          getDatabricksApiGetDatabricksWorkspaceStatus.invalidateQueries()
        },
      }
    )
  }

  const syncButton = (
    <TooltipButton tooltip="Sync jobs">
      <Button
        variant="ghost"
        size="icon"
        className={syncingWorkspaceJobs ? 'animate-spin' : ''}
        disabled={syncingWorkspaceJobs}
        onClick={onSync}
      >
        <IconTransformOutline className="size-5" />
      </Button>
    </TooltipButton>
  )

  return (
    <SectionContainer title="Jobs" action={syncButton}>
      <QueryErrorBoundary errorMessage="Failed to load jobs">
        <Suspense fallback={<LoadingComponent text="Loading jobs" />}>
          <DatabricksJobsSectionContent />
        </Suspense>
      </QueryErrorBoundary>
    </SectionContainer>
  )
}

function DatabricksJobsSectionContent() {
  const currentWorkspace = useCurrentWorkspace()
  const { workspaceId } = currentWorkspace
  const { getDatabricksApiGetDatabricksWorkspaceStatus } = useRestClient()
  const { data: workspace } = getDatabricksApiGetDatabricksWorkspaceStatus.useSuspenseQuery({ query: { workspaceId } })
  const { jobs } = workspace
  return jobs?.length === 0 ? (
    <NoSearchResultsDisplay text="No jobs found for this workspace" className="pt-0" />
  ) : (
    <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(350px,1fr))] gap-1">
      {jobs!.map(({ jobId, jobName, taskUrl, jobUrl, notebook }) => (
        <DatabricksJobCard
          key={jobId}
          jobId={jobId}
          jobName={jobName}
          taskUrl={taskUrl}
          jobUrl={jobUrl}
          notebook={notebook}
        />
      ))}
    </div>
  )
}

function DatabricksJobCard({ jobId, jobName, taskUrl, jobUrl, notebook }: DatabricksMessagesDatabricksWorkspaceJob) {
  const currentWorkspace = useCurrentWorkspace()
  const { workspaceId } = currentWorkspace
  const { putDatabricksApiRunDatabricksWorkspaceJobs, getDatabricksApiListJobRuns } = useRestClient()

  const { mutate: runJob, isPending: isRunning } = putDatabricksApiRunDatabricksWorkspaceJobs.useMutation()
  const invalidateRuns = () => getDatabricksApiListJobRuns.invalidateQueries()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [contextJson, setContextJson] = useState('')

  if (!jobId) {
    return null
  }

  const onConfirmRun = () => {
    const context = contextJson.trim()
    runJob(
      {
        query: { workspaceId: workspaceId, jobId: jobId },
        body: JSON.stringify(context || null),
      },
      {
        onError: (error) => {
          toast.error('Error running job: ' + getDatabricksErrorMessage(error))
        },
        onSuccess: () => {
          toast.success('Job running')
          setDialogOpen(false)
          setContextJson('')
        },
        onSettled: () => {
          invalidateRuns()
        },
      }
    )
  }

  return (
    <>
      <div key={jobId} className="grid grid-cols-[1fr_4fr_1fr] gap-2 rounded-lg bg-background p-4 shadow">
        <IconBg className="size-12">
          <IconDatabricksOutline className="size-6" />
        </IconBg>
        <div className="flex flex-col justify-between gap-1">
          <div className="text-base">{jobName}</div>
          <div className="text-xs font-light">{notebook}</div>
        </div>
        <div className="col-span-2 col-start-2 flex flex-row justify-end gap-2 border-t pt-3">
          <Button
            size="sm"
            className="h-6"
            onClick={() => setDialogOpen(true)}
          >
            <IconPlayOutline className="size-4" />
            <span className="ml-1 text-xs">Run</span>
          </Button>
          {jobUrl && (
            <a
              className={cn(buttonVariants({ size: 'sm', variant: 'outline' }), 'h-6 text-foreground')}
              href={jobUrl}
              target="_blank"
            >
              <IconEditOutline className="size-4" />
              <span className="ml-1 text-xs">Edit Job</span>
            </a>
          )}
          {taskUrl && (
            <a
              className={cn(buttonVariants({ size: 'sm', variant: 'outline' }), 'h-6 text-foreground')}
              href={taskUrl}
              target="_blank"
            >
              <IconEditOutline className="size-4" />
              <span className="ml-1 text-xs">Edit Task</span>
            </a>
          )}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Run {jobName}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="context-input" className="text-sm">
              Context (optional JSON)
            </Label>
            <Textarea
              id="context-input"
              placeholder='{"key": "value"}'
              value={contextJson}
              onChange={(e) => setContextJson(e.target.value)}
              rows={6}
              className="font-mono text-xs"
            />
            <p className="text-xs text-muted-foreground">
              Leave blank to use defaults. The notebook receives this as{' '}
              <code className="rounded bg-muted px-1">dbutils.widgets.get(&apos;context&apos;)</code>.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={onConfirmRun} disabled={isRunning}>
              <IconPlayOutline className="size-4" />
              <span className="ml-1">{isRunning ? 'Starting...' : 'Run'}</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
